// import node module libraries
import { Fragment, useState } from 'react';
import { Col, Row, Card, Badge, ProgressBar, Tab, Nav } from 'react-bootstrap';

import { useQuery } from '@apollo/client/react';
import { GET_ALL_COURSE_PROGRESS, GET_COURSE_BY_ID } from 'helper/graphql/queries';
import { useParams } from 'react-router-dom';
import useAuth from 'hooks/useAuth';
import EnrolledUsersTable from './EnrolledUsersTable';
import moment from 'moment';

const CourseProgress = () => {
	const { guid } = useParams();
	const { user } = useAuth();

	const [progressData, setProgressData] = useState([]);
	const [course, setCourse] = useState(null);

	useQuery(GET_COURSE_BY_ID, {
		variables: { guid: guid, userGuid: user?.guid },
		onCompleted: (data) => {
			const _course = data?.allCourses?.edges[0]?.node;
			setCourse(_course);
		},
	});

	useQuery(GET_ALL_COURSE_PROGRESS, {
		variables: { courseGuid: guid },
		onCompleted: (data) => {
			const _data = data?.allCourseProgress?.edges?.map(item => item.node) || [];
			setProgressData(_data);
		},
	});

	// Get all enrolled users from packagepayments
	const enrolledUsers = course?.packagepaymentsSet?.edges?.map(edge => edge.node) || [];
	
	// Merge enrolled users with their progress data
	const mergedData = enrolledUsers.map(enrolledUser => {
		const userProgress = progressData.find(progress => progress.user.guid === enrolledUser.user.guid);
		return {
			user: enrolledUser.user,
			courseProgress: userProgress?.courseProgress || "0",
			lastAccessedAt: userProgress?.lastAccessedAt || userProgress?.createdAt,
			createdAt: enrolledUsers.find(p => p.guid === enrolledUser.guid)?.createdAt || new Date().toISOString(),
			// hasProgress: !!userProgress
		};
	});

	// Calculate stats
	const enrolledCount = enrolledUsers.length;
	const completedCount = mergedData.filter(item => parseFloat(item.courseProgress || 0) >= 100).length;
	const inProgressCount = mergedData.filter(item => {
		const progress = parseFloat(item.courseProgress || 0);
		return progress > 0 && progress < 100;
	}).length;
	const notStartedCount = mergedData.filter(item => parseFloat(item.courseProgress || 0) === 0).length;
	
	const avgProgress = mergedData.length > 0 
		? (mergedData.reduce((sum, item) => sum + parseFloat(item.courseProgress || 0), 0) / mergedData.length).toFixed(1)
		: 0;

	return (
		<Fragment>
			{/* Page Header */}
			<div className="progress-page-header mb-4">
				<Row className="align-items-start">
					<Col lg={8} md={12}>
						<div className="d-flex align-items-start gap-3">
							{course?.introImage?.[0] && (
								<div className="course-thumbnail-header">
									<img 
										src={course.introImage[0]} 
										alt={course.title}
										onError={(e) => {
											e.target.src = '/assets/images/course/course-default.jpg';
										}}
									/>
								</div>
							)}
							<div className="flex-grow-1">
								<div className="d-flex align-items-center gap-2 mb-2">
									<Badge bg="" className="status-badge-header">
										<i className="fe fe-activity me-1"></i>
										Active Course
									</Badge>
									{course?.dificultyLevel && (
										<Badge bg="" className="level-badge-header">
											{course.dificultyLevel}
										</Badge>
									)}
								</div>
								<h1 className="page-title mb-2">
									{course?.title || 'Course Progress'}
								</h1>
								<div className="course-meta-header">
									<span className="meta-item">
										<i className="fe fe-calendar me-1"></i>
										{moment(course?.courseStartDate).format("MMM DD")} - {moment(course?.courseEndDate).format("MMM DD, YYYY")}
									</span>
									<span className="meta-item">
										<i className="fe fe-user me-1"></i>
										{course?.creator?.firstName} {course?.creator?.lastName}
									</span>
								</div>
							</div>
						</div>
					</Col>
					<Col lg={4} md={12} className="mt-3 mt-lg-0">
						<Card className="avg-progress-card">
							<Card.Body className="p-3">
								<div className="text-center">
									<div className="avg-label">Average Progress</div>
									<div className="avg-value">{avgProgress}%</div>
									<ProgressBar 
										now={avgProgress} 
										variant="info"
										className="avg-progress-bar mt-2"
									/>
								</div>
							</Card.Body>
						</Card>
					</Col>
				</Row>
			</div>

			{/* Stats Cards */}
			<Row className="g-3 mb-4">
				<Col lg={3} md={6} sm={12}>
					<Card className="stats-card-progress stats-enrolled">
						<Card.Body className="p-4">
							<div className="d-flex align-items-center justify-content-between">
								<div>
									<div className="stats-label">Total Enrolled</div>
									<div className="stats-value">{enrolledCount}</div>
								</div>
								<div className="stats-icon-wrapper stats-icon-enrolled">
									<i className="fe fe-users"></i>
								</div>
							</div>
						</Card.Body>
					</Card>
				</Col>
				<Col lg={3} md={6} sm={12}>
					<Card className="stats-card-progress stats-completed">
						<Card.Body className="p-4">
							<div className="d-flex align-items-center justify-content-between">
								<div>
									<div className="stats-label">Completed</div>
									<div className="stats-value">{completedCount}</div>
									<div className="stats-percentage">
										{enrolledCount > 0 ? ((completedCount / enrolledCount) * 100).toFixed(0) : 0}%
									</div>
								</div>
								<div className="stats-icon-wrapper stats-icon-completed">
									<i className="fe fe-check-circle"></i>
								</div>
							</div>
						</Card.Body>
					</Card>
				</Col>
				<Col lg={3} md={6} sm={12}>
					<Card className="stats-card-progress stats-inprogress">
						<Card.Body className="p-4">
							<div className="d-flex align-items-center justify-content-between">
								<div>
									<div className="stats-label">In Progress</div>
									<div className="stats-value">{inProgressCount}</div>
									<div className="stats-percentage">
										{enrolledCount > 0 ? ((inProgressCount / enrolledCount) * 100).toFixed(0) : 0}%
									</div>
								</div>
								<div className="stats-icon-wrapper stats-icon-inprogress">
									<i className="fe fe-trending-up"></i>
								</div>
							</div>
						</Card.Body>
					</Card>
				</Col>
				<Col lg={3} md={6} sm={12}>
					<Card className="stats-card-progress stats-notstarted">
						<Card.Body className="p-4">
							<div className="d-flex align-items-center justify-content-between">
								<div>
									<div className="stats-label">Not Started</div>
									<div className="stats-value">{notStartedCount}</div>
									<div className="stats-percentage">
										{enrolledCount > 0 ? ((notStartedCount / enrolledCount) * 100).toFixed(0) : 0}%
									</div>
								</div>
								<div className="stats-icon-wrapper stats-icon-notstarted">
									<i className="fe fe-clock"></i>
								</div>
							</div>
						</Card.Body>
					</Card>
				</Col>
			</Row>

			{/* Enrolled Users with Progress Table */}
			<Tab.Container defaultActiveKey="all">
				<Card className="progress-table-card">
					<Card.Header className="progress-table-header">
						<Row className="align-items-center">
							<Col lg={6} md={12}>
								<h5 className="mb-1">Enrolled Students</h5>
								<p className="text-muted mb-0" style={{fontSize: '0.875rem'}}>
									View all enrolled students and track their progress
								</p>
							</Col>
							<Col lg={6} md={12} className="mt-3 mt-lg-0">
								<Nav className="progress-tabs-nav">
									<Nav.Item>
										<Nav.Link eventKey="all" className="progress-tab-link">
											All Students
											<Badge bg="" className="tab-count-badge ms-2">{enrolledCount}</Badge>
										</Nav.Link>
									</Nav.Item>
									<Nav.Item>
										<Nav.Link eventKey="completed" className="progress-tab-link">
											Completed
											<Badge bg="" className="tab-count-badge tab-count-success ms-2">{completedCount}</Badge>
										</Nav.Link>
									</Nav.Item>
									<Nav.Item>
										<Nav.Link eventKey="inprogress" className="progress-tab-link">
											In Progress
											<Badge bg="" className="tab-count-badge tab-count-warning ms-2">{inProgressCount}</Badge>
										</Nav.Link>
									</Nav.Item>
									<Nav.Item>
										<Nav.Link eventKey="notstarted" className="progress-tab-link">
											Not Started
											<Badge bg="" className="tab-count-badge tab-count-secondary ms-2">{notStartedCount}</Badge>
										</Nav.Link>
									</Nav.Item>
								</Nav>
							</Col>
						</Row>
					</Card.Header>
					<Card.Body className="p-0">
						<Tab.Content>
							<Tab.Pane eventKey="all">
								<EnrolledUsersTable enrolledUsers={mergedData} courseGuid={guid} />
							</Tab.Pane>
							<Tab.Pane eventKey="completed">
								<EnrolledUsersTable
									enrolledUsers={mergedData.filter(item => parseFloat(item.courseProgress || 0) >= 100)}
									courseGuid={guid}
								/>
							</Tab.Pane>
							<Tab.Pane eventKey="inprogress">
								<EnrolledUsersTable
									enrolledUsers={mergedData.filter(item => {
										const progress = parseFloat(item.courseProgress || 0);
										return progress > 0 && progress < 100;
									})}
									courseGuid={guid}
								/>
							</Tab.Pane>
							<Tab.Pane eventKey="notstarted">
								<EnrolledUsersTable
									enrolledUsers={mergedData.filter(item => parseFloat(item.courseProgress || 0) === 0)}
									courseGuid={guid}
								/>
							</Tab.Pane>
						</Tab.Content>
					</Card.Body>
				</Card>
			</Tab.Container>

			<style jsx>{`
				/* Page Header */
				.progress-page-header {
					background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
					padding: 2rem;
					border-radius: 16px;
					border-left: 4px solid #F68521;
				}

				.course-thumbnail-header {
					width: 120px;
					height: 90px;
					border-radius: 12px;
					overflow: hidden;
					flex-shrink: 0;
					box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
				}

				.course-thumbnail-header img {
					width: 100%;
					height: 100%;
					object-fit: cover;
				}

				:global(.status-badge-header) {
					background: rgba(16, 185, 129, 0.1) !important;
					color: #10b981 !important;
					padding: 6px 12px;
					border-radius: 8px;
					font-size: 0.75rem;
					font-weight: 700;
					text-transform: uppercase;
					letter-spacing: 0.5px;
					display: inline-flex;
					align-items: center;
				}

				:global(.level-badge-header) {
					background: rgba(29, 158, 176, 0.1) !important;
					color: #1d9eb0 !important;
					padding: 6px 12px;
					border-radius: 8px;
					font-size: 0.75rem;
					font-weight: 700;
				}

				.page-title {
					font-size: 1.75rem;
					font-weight: 800;
					color: #1e293b;
					line-height: 1.3;
				}

				.course-meta-header {
					display: flex;
					flex-wrap: wrap;
					gap: 1.5rem;
					font-size: 0.875rem;
					color: #64748b;
				}

				.meta-item {
					display: flex;
					align-items: center;
				}

				/* Average Progress Card */
				:global(.avg-progress-card) {
					border: none;
					border-radius: 12px;
					background: linear-gradient(135deg, #F68521 0%, #1d9eb0 100%);
					color: white;
				}

				.avg-label {
					font-size: 0.8125rem;
					font-weight: 600;
					text-transform: uppercase;
					letter-spacing: 0.5px;
					opacity: 0.9;
					margin-bottom: 0.5rem;
				}

				.avg-value {
					font-size: 2.5rem;
					font-weight: 800;
					line-height: 1;
					margin-bottom: 0.75rem;
				}

				:global(.avg-progress-bar) {
					height: 8px;
					border-radius: 10px;
					background: rgba(255, 255, 255, 0.2);
				}

				:global(.avg-progress-bar .progress-bar) {
					background: white;
					border-radius: 10px;
				}

				/* Stats Cards */
				:global(.stats-card-progress) {
					border: none;
					border-radius: 12px;
					transition: all 0.3s ease;
					position: relative;
					overflow: hidden;
				}

				:global(.stats-card-progress::before) {
					content: '';
					position: absolute;
					top: 0;
					left: 0;
					width: 4px;
					height: 100%;
					background: currentColor;
				}

				:global(.stats-card-progress:hover) {
					transform: translateY(-4px);
					box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
				}

				:global(.stats-enrolled::before) {
					background: #1d9eb0;
				}

				:global(.stats-completed::before) {
					background: #10b981;
				}

				:global(.stats-inprogress::before) {
					background: #fbbf24;
				}

				:global(.stats-notstarted::before) {
					background: #94a3b8;
				}

				.stats-label {
					font-size: 0.8125rem;
					color: #94a3b8;
					font-weight: 600;
					text-transform: uppercase;
					letter-spacing: 0.5px;
					margin-bottom: 0.5rem;
				}

				.stats-value {
					font-size: 2rem;
					font-weight: 800;
					color: #1e293b;
					line-height: 1;
				}

				.stats-percentage {
					font-size: 0.875rem;
					color: #64748b;
					font-weight: 600;
					margin-top: 0.25rem;
				}

				.stats-icon-wrapper {
					width: 56px;
					height: 56px;
					display: flex;
					align-items: center;
					justify-content: center;
					border-radius: 12px;
					font-size: 1.75rem;
				}

				.stats-icon-enrolled {
					background: rgba(29, 158, 176, 0.1);
					color: #1d9eb0;
				}

				.stats-icon-completed {
					background: rgba(16, 185, 129, 0.1);
					color: #10b981;
				}

				.stats-icon-inprogress {
					background: rgba(251, 191, 36, 0.1);
					color: #fbbf24;
				}

				.stats-icon-notstarted {
					background: rgba(148, 163, 184, 0.1);
					color: #94a3b8;
				}

				/* Progress Table Card */
				:global(.progress-table-card) {
					border: none;
					border-radius: 16px;
					box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
					overflow: hidden;
				}

				:global(.progress-table-header) {
					background: white;
					border-bottom: 2px solid #f1f5f9;
					padding: 1.5rem;
				}

				:global(.progress-table-header) h5 {
					font-size: 1.125rem;
					font-weight: 700;
					color: #1e293b;
					margin: 0;
				}

				/* Progress Tabs */
				:global(.progress-tabs-nav) {
					display: flex;
					gap: 0.5rem;
					flex-wrap: wrap;
					justify-content: flex-end;
				}

				:global(.progress-tab-link) {
					padding: 0.5rem 1rem !important;
					border-radius: 8px !important;
					border: 2px solid transparent !important;
					font-weight: 600 !important;
					font-size: 0.8125rem !important;
					color: #64748b !important;
					transition: all 0.3s ease !important;
					display: flex !important;
					align-items: center !important;
					background: #f8fafc !important;
				}

				:global(.progress-tab-link:hover) {
					background: white !important;
					color: #F68521 !important;
					border-color: #f1f5f9 !important;
				}

				:global(.progress-tab-link.active) {
					background: linear-gradient(135deg, #F68521 0%, #1d9eb0 100%) !important;
					color: white !important;
					border-color: transparent !important;
					box-shadow: 0 2px 8px rgba(246, 133, 33, 0.3) !important;
				}

				:global(.progress-tab-link.active .tab-count-badge) {
					background: rgba(255, 255, 255, 0.25) !important;
					color: white !important;
				}

				/* Tab Count Badges */
				:global(.tab-count-badge) {
					padding: 3px 8px;
					border-radius: 6px;
					font-size: 0.6875rem;
					font-weight: 700;
					background: rgba(100, 116, 139, 0.1) !important;
					color: #64748b !important;
				}

				:global(.tab-count-success) {
					background: rgba(16, 185, 129, 0.1) !important;
					color: #10b981 !important;
				}

				:global(.tab-count-warning) {
					background: rgba(251, 191, 36, 0.1) !important;
					color: #fbbf24 !important;
				}

				:global(.tab-count-secondary) {
					background: rgba(148, 163, 184, 0.1) !important;
					color: #94a3b8 !important;
				}

				/* Responsive */
				@media (max-width: 991px) {
					.page-title {
						font-size: 1.5rem;
					}

					.progress-page-header {
						padding: 1.5rem;
					}

					.course-thumbnail-header {
						width: 100px;
						height: 75px;
					}

					:global(.progress-tabs-nav) {
						justify-content: flex-start;
						margin-top: 1rem;
					}
				}

				@media (max-width: 768px) {
					.stats-value {
						font-size: 1.75rem;
					}

					.avg-value {
						font-size: 2rem;
					}

					.course-meta-header {
						flex-direction: column;
						gap: 0.5rem;
					}

					:global(.progress-tab-link) {
						padding: 0.5rem 0.75rem !important;
						font-size: 0.75rem !important;
					}
				}
			`}</style>
		</Fragment>
	);
};

export default CourseProgress;